import { useScienceTutorSection } from './scienceTutorContext';
import { ArrowRight } from 'lucide-react';
import { useState } from 'react';

import {
  ADDITION_MONOMERS,
  additionDiagram,
  additionQuestion,
  formatFormula,
  monomerCounts,
  monomerText,
  repeatUnitCounts,
  repeatUnitText,
  type AdditionDirection,
  type AdditionMonomer,
} from './polymersBiomolecules';
import { ChoicePanel, StructureDiagramView } from './PolymersShared';
import { CARD_CLASS, chipClass } from './polymersStyles';

const DIRECTIONS: { id: AdditionDirection; label: string }[] = [
  { id: 'monomer-to-polymer', label: 'Monomer → repeat unit' },
  { id: 'polymer-to-monomer', label: 'Repeat unit → monomer' },
];

function caseLabel(monomer: AdditionMonomer, index: number, direction: AdditionDirection) {
  return direction === 'monomer-to-polymer' ? monomer.monomerName : `Unknown polymer ${index + 1}`;
}

function AdditionReveal({ monomer }: { monomer: AdditionMonomer }) {
  return (
    <section className="mt-5 rounded-[1.75rem] border border-brand-mint/30 bg-wash-mint p-5">
      <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-mint">
        Model revealed
      </p>
      <div className="mt-3 grid items-center gap-4 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
        <StructureDiagramView
          diagram={additionDiagram(monomer, 'monomer')}
          caption={`${monomer.monomerName} · ${monomerText(monomer)}`}
        />
        <ArrowRight
          className="mx-auto h-6 w-6 rotate-90 text-brand-coral md:rotate-0"
          aria-hidden="true"
        />
        <StructureDiagramView
          diagram={additionDiagram(monomer, 'repeat-unit')}
          caption={`Repeat unit · ${repeatUnitText(monomer)}`}
        />
      </div>
      <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
        <div className="rounded-2xl bg-white/80 p-3">
          <dt className="text-xs font-extrabold uppercase tracking-wide text-slate2">Polymer</dt>
          <dd className="mt-1 font-extrabold text-ink">
            {monomer.polymerName}
            {monomer.abbreviation ? ` (${monomer.abbreviation})` : ''}
          </dd>
        </div>
        <div className="rounded-2xl bg-white/80 p-3">
          <dt className="text-xs font-extrabold uppercase tracking-wide text-slate2">
            Atoms per unit
          </dt>
          <dd className="mt-1 font-mono font-extrabold text-ink">
            {formatFormula(monomerCounts(monomer))} → {formatFormula(repeatUnitCounts(monomer))}
          </dd>
        </div>
        <div className="rounded-2xl bg-white/80 p-3">
          <dt className="text-xs font-extrabold uppercase tracking-wide text-slate2">Common use</dt>
          <dd className="mt-1 font-bold text-ink">{monomer.commonUse}</dd>
        </div>
      </dl>
      <p className="mt-3 text-sm font-medium leading-6 text-slate2">
        The highlighted C=C becomes a single C–C bond and two new bonds (dashed) link each unit to
        its neighbours. Nothing is lost, so the repeat unit has the same formula as the monomer.
      </p>
    </section>
  );
}

function AdditionCase({
  monomer,
  index,
  direction,
}: {
  monomer: AdditionMonomer;
  index: number;
  direction: AdditionDirection;
}) {
  const [revealed, setRevealed] = useState(false);
  useScienceTutorSection('AdditionCase', { revealed });
  const question = additionQuestion(monomer.id, direction);
  if (!question) return null;
  const forward = direction === 'monomer-to-polymer';

  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
      <article className={CARD_CLASS}>
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">
          {forward ? 'Given monomer' : 'Given repeat unit'}
        </p>
        <h3 className="mt-2 text-2xl font-extrabold text-ink">
          {forward ? monomer.monomerName : caseLabel(monomer, index, direction)}
        </h3>
        {forward && monomer.otherName ? (
          <p className="text-sm font-bold text-slate2">also called {monomer.otherName}</p>
        ) : null}
        <div className="mt-4 rounded-[1.5rem] border border-brand-sky/20 bg-wash-sky p-4">
          <StructureDiagramView
            diagram={additionDiagram(monomer, forward ? 'monomer' : 'repeat-unit')}
            caption={forward ? monomerText(monomer) : repeatUnitText(monomer)}
          />
        </div>
        <p className="mt-4 text-sm font-medium leading-6 text-slate2">
          {forward
            ? 'Find the C=C. In addition polymerisation it opens, and each carbon bonds to the next monomer.'
            : 'Look at the two backbone carbons inside the brackets. The monomer had a C=C between them.'}
        </p>
      </article>
      <article className={CARD_CLASS}>
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
          Predict
        </p>
        <div className="mt-3">
          <ChoicePanel question={question} monoOptions onChecked={() => setRevealed(true)} />
        </div>
      </article>
      {revealed ? (
        <div className="xl:col-span-2">
          <AdditionReveal monomer={monomer} />
        </div>
      ) : null}
    </div>
  );
}

export function PolymersAdditionMode() {
  const [direction, setDirection] = useState<AdditionDirection>('monomer-to-polymer');
  const [monomerId, setMonomerId] = useState(ADDITION_MONOMERS[0].id);
  useScienceTutorSection('PolymersAdditionMode', { direction, monomerId });
  const index = ADDITION_MONOMERS.findIndex((item) => item.id === monomerId);
  const monomer = ADDITION_MONOMERS[index];

  return (
    <div className="space-y-6">
      <section className={CARD_CLASS}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">
              1 · Choose a direction and a case
            </p>
            <h2 className="mt-2 text-2xl font-extrabold text-ink">Addition polymers</h2>
          </div>
          <div className="grid gap-2 sm:grid-cols-2" role="group" aria-label="Question direction">
            {DIRECTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                aria-pressed={direction === option.id}
                onClick={() => setDirection(option.id)}
                className={chipClass(direction === option.id)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        <div
          className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6"
          role="group"
          aria-label="Addition polymer case"
        >
          {ADDITION_MONOMERS.map((item, itemIndex) => (
            <button
              key={item.id}
              type="button"
              aria-pressed={monomerId === item.id}
              onClick={() => setMonomerId(item.id)}
              className={chipClass(monomerId === item.id)}
            >
              {caseLabel(item, itemIndex, direction)}
            </button>
          ))}
        </div>
      </section>
      <AdditionCase
        key={`${direction}-${monomer.id}`}
        monomer={monomer}
        index={index}
        direction={direction}
      />
    </div>
  );
}
